// Surface 01 — la pile du matin.
//
// UNE PAGE QUI DOIT POUVOIR ATTEINDRE ZÉRO. C'est sa seule règle, et tout le
// reste en découle.
//
// L'écran précédent (« Veille & décisions ») faisait deux métiers à la fois :
// trier ce qui est arrivé cette nuit, et suivre les propales en cours. Comme
// les deux vivaient dans la même table, il fallait six listes déroulantes pour
// retrouver l'un dans l'autre. Séparés, chacun n'a plus qu'une chose à montrer.
//
// CE QUI ENTRE ICI : les offres actives, jamais tranchées, sur lesquelles au
// moins un profil du vivier est ressorti. Une offre que personne ne peut tenir
// n'est pas une décision à prendre, c'est de la documentation : elle vit en
// Veille. Sans ce filtre la pile ferait 610 lignes au lieu de 153.
//
// CE QUI EN SORT : les trois gestes, et rien d'autre. Proposer quelqu'un
// (l'offre part en suivi), plus tard (réserve), écarter. Ouvrir une offre pour
// la lire ne la fait PAS sortir : un coup d'œil n'est pas une décision.
//
// LA FORME, ET POURQUOI ELLE A CHANGÉ DEUX FOIS.
//
// D'abord des cartes de 60 px : neuf offres par écran sur cent cinquante,
// seize écrans pour voir la pile, le score en troisième position visuelle
// après une case à cocher et un logo. Corrigé par une ligne dense qui tenait
// vingt-cinq offres par écran, chiffre en tête, actions au survol seulement.
//
// Puis la refonte Radar (2026-08-25) a introduit un SECOND format, des cartes
// hautes de 150-200 px, pour trois offres par nuit sans filtre ni case à
// cocher. Deux écrans qui ne se ressemblaient plus du tout pour la même
// tâche, retour de Nicolas après coup : les cartes Radar « beaucoup trop
// grandes », la liste Console « pas assez d'actions » (les trois boutons
// cachés au survol, justement).
//
// LIGNEOFFRE EST LE FORMAT UNIQUE DEPUIS. Un compromis assumé, pas un optimum
// pour chaque bout : plus compact que les cartes Radar, plus riche en actions
// visibles (jamais cachées au survol) que l'ancienne ligne dense. Il sert les
// deux formules ; seuls les filtres, la case à cocher et le lot restent
// propres à la Console (ils ne servent à rien à trois offres par nuit).
//
// LES PALIERS NE SONT PAS INVENTÉS ICI. Ils dérivent de `scoreTone`, seule
// source des seuils du produit (70 et 40). Ce dépôt a déjà vécu deux jeux de
// seuils concurrents — un 72 vert dans l'extension et orange dans la console —
// et un troisième découpage propre à cet écran rouvrirait exactement ça.

import { useMemo, useState } from 'react'
import {
  Check, ChevronDown, ChevronRight, Clock3, EyeOff, ExternalLink, MapPin, Plus, RotateCcw, Search, Undo2, X,
} from 'lucide-react'
import { Checkbox, ScoreRing, scoreTone, type ScoreTone } from '@ai-job-fit/ui'
import { get, post, type OfferRow, type Summary } from '../lib/api'
import { relTime, initials, teinte } from '../lib/format'
import { navigate, useQuery } from '../lib/router'
import { useData, Loading, Empty, Score, ActionButton, useToast } from '../components/common'
import { ProposerProfil } from '../components/candidature'
import { SaisieManuelle } from './Veille'
import { useProfilUnique } from '../lib/formule'
import { useMots } from '../lib/vocabulaire'
import { useT } from '../lib/i18n'
import { Employeur, LogoEntreprise, jourDeLecture, mentionSignaux } from '../components/sites'

type Periode = 'all' | 'today' | '7d'

function usePeriodes(): { id: Periode; label: string; days: number | null }[] {
  const t = useT()
  return [
    { id: 'all', label: t('veille.periodAll'), days: null },
    { id: 'today', label: t('veille.periodToday'), days: 1 },
    { id: '7d', label: t('veille.period7d'), days: 7 },
  ]
}

/**
 * Les paliers de la pile, dans l'ordre de lecture.
 *
 * Un par ton de `scoreTone`, et rien d'autre : c'est ce qui garantit qu'une
 * ligne verte est toujours dans le groupe « bonne correspondance », sans
 * qu'aucun seuil ne soit réécrit ici.
 */
function usePaliers(): { ton: ScoreTone; titre: string; borne: string }[] {
  const t = useT()
  return [
    { ton: 'good', titre: t('decisions.tierGood'), borne: t('decisions.tierGoodRange') },
    { ton: 'mid', titre: t('decisions.tierMid'), borne: t('decisions.tierMidRange') },
    { ton: 'low', titre: t('decisions.tierLow'), borne: t('decisions.tierLowRange') },
  ]
}

const meilleurScore = (o: OfferRow) =>
  o.matches.length ? Math.max(...o.matches.map((m) => m.score)) : -1

export function Decisions({
  summary,
  onChanged,
}: {
  summary: Summary | null
  onChanged: () => void
}) {
  const mots = useMots()
  const t = useT()
  const PERIODES = usePeriodes()
  const PALIERS = usePaliers()
  const offers = useData(() => get<OfferRow[]>('/api/offers?scope=pile'), [])
  const [params, setParams] = useQuery()
  const [selection, setSelection] = useState<Set<number>>(new Set())
  const [manualOpen, setManualOpen] = useState(false)
  const toast = useToast()

  // Les filtres vivent dans l'URL : ils survivent au retour depuis une offre,
  // qui est le geste le plus fréquent de cet écran.
  const q = params.get('q') ?? ''
  const consultant = params.get('consultant') ?? 'all'
  const entreprise = params.get('entreprise') ?? 'all'
  const palier = params.get('palier') ?? 'all'
  const periode = (params.get('periode') as Periode) ?? 'all'

  const toutes = offers.data ?? []

  // Les listes déroulantes viennent de la pile RÉELLE : proposer un filtre qui
  // ne renverrait rien n'aide personne.
  const listes = useMemo(() => {
    const uniq = (xs: (string | null)[]) =>
      [...new Set(xs.filter((x): x is string => !!x && x.trim() !== ''))].sort((a, b) => a.localeCompare(b, 'fr'))
    return {
      consultants: uniq(toutes.flatMap((o) => o.matches.map((m) => m.name))),
      entreprises: uniq(toutes.map((o) => o.company).filter((c): c is string => !!c)),
    }
  }, [toutes])

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const jours = PERIODES.find((p) => p.id === periode)?.days ?? null
    const limite = jours ? Date.now() - jours * 86400_000 : null

    const retenues = toutes.filter((o) => {
      if (consultant !== 'all' && !o.matches.some((m) => m.name === consultant)) return false
      if (entreprise !== 'all' && o.company !== entreprise) return false
      if (palier !== 'all' && scoreTone(meilleurScore(o)) !== palier) return false
      if (limite && new Date(o.first_seen_at.replace(' ', 'T') + 'Z').getTime() < limite) return false
      if (needle && !`${o.company ?? ''} ${o.source_name} ${o.title} ${o.location ?? ''}`.toLowerCase().includes(needle)) return false
      return true
    })

    // PAR MEILLEUR PROFIL D'ABORD, jamais par date. La question du matin est
    // « qui puis-je placer », pas « qu'est-ce qui est arrivé » : trié par
    // fraîcheur, on lit d'abord dix offres à 18/100 et on referme l'écran
    // avant d'avoir vu celle à 84. À score égal, la plus récente en tête.
    return [...retenues].sort(
      (a, b) => meilleurScore(b) - meilleurScore(a) || -a.first_seen_at.localeCompare(b.first_seen_at)
    )
  }, [toutes, q, consultant, entreprise, palier, periode])

  const groupes = useMemo(
    () => PALIERS.map((p) => ({ ...p, offres: rows.filter((o) => scoreTone(meilleurScore(o)) === p.ton) })),
    [rows]
  )

  const filtresActifs =
    [consultant, entreprise, palier].filter((v) => v !== 'all').length +
    (periode !== 'all' ? 1 : 0) + (q ? 1 : 0)
  const reinitialiser = () =>
    setParams({ consultant: null, entreprise: null, palier: null, periode: null, q: null })

  const idsVisibles = rows.map((o) => o.id)
  const selectionne = idsVisibles.filter((id) => selection.has(id))
  const toutSelectionne = idsVisibles.length > 0 && selectionne.length === idsVisibles.length

  function rafraichir() {
    setSelection(new Set())
    offers.reload()
    onChanged()
  }

  async function trancher(o: OfferRow, status: 'later' | 'skip') {
    await post(`/api/offers/${o.id}/status`, { status })
    toast(t(
      status === 'later' ? 'decisions.laterToast' : 'decisions.skippedToast',
      { title: o.title }
    ))
    rafraichir()
  }

  async function traiterLot(action: 'skip' | 'later', libelle: string) {
    await post('/api/offers/batch', { ids: selectionne, action })
    toast(t('decisions.batchToast', { n: selectionne.length, action: libelle }))
    rafraichir()
  }

  function basculer(id: number) {
    setSelection((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  if (offers.loading) return <Loading label={t('decisions.loading')} />
  if (offers.error) return <Empty>{offers.error}</Empty>

  const bonnes = toutes.filter((o) => scoreTone(meilleurScore(o)) === 'good').length

  return (
    <>
      <div className="bar">
        <div>
          <h3>{mots('pile.titre')}</h3>
          <div className="sub">
            {/* Deux moitiés indépendantes plutôt qu'une phrase à deux trous :
                le nombre d'offres et le nombre de bonnes correspondances
                s'accordent SÉPARÉMENT, et une seule chaîne aurait demandé
                quatre variantes pour éviter le « 1 bonne(s) correspondance(s) »
                qui traînait ici. */}
            {toutes.length === 0
              ? t('decisions.nothingPending')
              : `${t(toutes.length > 1 ? 'decisions.countPlural' : 'decisions.countSingular', { n: toutes.length })} · ${t(bonnes > 1 ? 'decisions.goodPlural' : 'decisions.goodSingular', { n: bonnes })}`}
            {summary?.lastRun && ` · ${t('decisions.lastRun', { when: relTime(summary.lastRun) })}`}
          </div>
        </div>
        <div className="grow" />
        <div className="search">
          <Search size={14} />
          <input
            value={q}
            onChange={(e) => setParams({ q: e.target.value })}
            placeholder={t('veille.searchPlaceholder')}
            aria-label={t('decisions.searchAria')}
          />
          {q && (
            <button className="searchclear" onClick={() => setParams({ q: null })} aria-label={t('veille.clearSearch')}>
              <X size={13} />
            </button>
          )}
        </div>
        <button className="btn-secondary" onClick={() => setManualOpen(true)}>
          <Plus size={15} />{t('veille.manualEntryButton')}
        </button>
      </div>

      <div className="body">
        {toutes.length === 0 ? (
          // La pile vide n'est pas un état d'erreur, c'est l'objectif : elle se
          // félicite et pointe la suite, au lieu d'afficher « aucun résultat »
          // comme le ferait un filtre qui n'a rien trouvé.
          <div className="pilezero">
            <span className="pz-ic"><Check size={26} /></span>
            <h4>{mots('pile.vide')}</h4>
            <p>
              {t('decisions.pileZeroText')}{' '}
              <button className="linkbtn" onClick={() => navigate('#/suivi')}>{t('decisions.pileZeroTracking')}</button>,{' '}
              {t('decisions.pileZeroText2')}{' '}
              <button className="linkbtn" onClick={() => navigate('#/veille')}>{t('decisions.pileZeroWatch')}</button>.
            </p>
          </div>
        ) : (
          <>
            {/* Filtres et sélection multiple : utiles à cent cinquante lignes,
                inutiles à trois. Cachés en Radar plutôt que retirés du code :
                la ligne qui suit reste EXACTEMENT la même des deux côtés,
                c'est tout le point de cette refonte. */}
            {(
              <div className="filters">
                <Select value={consultant} onChange={(v) => setParams({ consultant: v })} all={t('veille.allConsultants')} options={listes.consultants} />
                <Select value={entreprise} onChange={(v) => setParams({ entreprise: v })} all={t('veille.allCompanies')} options={listes.entreprises} />
                <select
                  className={`chip${palier !== 'all' ? ' on' : ''}`}
                  value={palier}
                  onChange={(e) => setParams({ palier: e.target.value })}
                  aria-label={t('decisions.tierAria')}
                >
                  <option value="all">{t('decisions.allScores')}</option>
                  {PALIERS.map((p) => <option key={p.ton} value={p.ton}>{p.titre} ({p.borne})</option>)}
                </select>
                <select
                  className={`chip${periode !== 'all' ? ' on' : ''}`}
                  value={periode}
                  onChange={(e) => setParams({ periode: e.target.value })}
                  aria-label={t('veille.periodAria')}
                >
                  {PERIODES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>

                <div className="grow" />
                {filtresActifs > 0 && (
                  <button className="chip" onClick={reinitialiser}>
                    <RotateCcw size={12} />{t('veille.reset', { n: filtresActifs })}
                  </button>
                )}
              </div>
            )}

            {(
              <div className="dl-head">
                <Checkbox
                  label={t('decisions.selectAll')}
                  checked={toutSelectionne}
                  indeterminate={selectionne.length > 0 && !toutSelectionne}
                  onChange={(c) => setSelection(new Set(c ? idsVisibles : []))}
                />
                {selectionne.length > 0 ? (
                  <>
                    <strong>{t(selectionne.length > 1 ? 'decisions.selectedPlural' : 'decisions.selectedSingular', { n: selectionne.length })}</strong>
                    <div className="grow" />
                    <ActionButton size="sm" onClick={() => traiterLot('later', t('decisions.batchLater'))}>
                      <Clock3 size={13} />{t('decisions.later')}
                    </ActionButton>
                    <ActionButton size="sm" onClick={() => traiterLot('skip', t('decisions.batchSkipped'))}>
                      <EyeOff size={13} />{t('decisions.skip')}
                    </ActionButton>
                    <button className="chip" onClick={() => setSelection(new Set())}>{t('decisions.deselect')}</button>
                  </>
                ) : (
                  <>
                    <span className="dl-hint">{t('decisions.selectHint')}</span>
                    <div className="grow" />
                    <span className="filtercount">{t('decisions.shownOfTotal', { shown: rows.length, total: toutes.length })}</span>
                  </>
                )}
              </div>
            )}

            {rows.length === 0 ? (
              <Empty>
                {t('decisions.noMatch')}{' '}
                <button className="linkbtn" onClick={reinitialiser}>{t('decisions.resetShort')}</button>
              </Empty>
            ) : (
              groupes.map((g) =>
                g.offres.length === 0 ? null : (
                  <section className="dl-group" key={g.ton}>
                    {/* L'en-tête de palier répond à « où j'arrête ce matin »,
                        qui est la vraie question, sans avoir à lire une seule
                        ligne pour la trancher. */}
                    <div className={`dl-gh t-${g.ton}`}>
                      <span className="dl-dot" />
                      <b>{g.titre}</b>
                      <span className="dl-borne">{g.borne}</span>
                      <span className="dl-rule" />
                      <span className="dl-n">{g.offres.length}</span>
                    </div>
                    <div className="dl-rows">
                      {g.offres.map((o) => (
                        <LigneOffre
                          key={o.id}
                          offre={o}
                          choisie={selection.has(o.id)}
                          onBasculer={() => basculer(o.id)}
                          onTrancher={trancher}
                          onDone={rafraichir}
                        />
                      ))}
                    </div>
                  </section>
                )
              )
            )}
          </>
        )}

        {/* Écartées, réunies ici plutôt que seulement dans Veille/Toutes les
            offres : Nicolas a signalé qu'« Pas pour moi » faisait disparaître
            une offre sans moyen de revenir dessus depuis cet écran. Repliée
            par défaut, chargée à part (scope=skip) : revenir sur une décision
            reste un geste rare, pas de raison de payer cette requête à chaque
            ouverture de la pile. */}
        <Ecartees onDone={rafraichir} />
      </div>

      {manualOpen && <SaisieManuelle onClose={() => setManualOpen(false)} onDone={onChanged} />}
    </>
  )
}

/**
 * Une offre à trancher, dans le format commun aux deux formules.
 *
 * Compromis assumé entre les deux formats qu'il remplace, pas un optimum pour
 * chacun : l'anneau est petit (34 px, le format « liste » prévu dès l'origine
 * dans `ScoreRing`) plutôt que la grande carte Radar, et les trois actions
 * sont TOUJOURS visibles, en toutes lettres, plutôt que cachées au survol
 * comme l'ancienne ligne dense : c'était le reproche précis sur la Console.
 *
 * Ce qu'elle montre sert à trancher : le score, le logo et le nom de
 * l'employeur, le LIEU en clair, l'âge, le point bloquant s'il y en a un.
 * Plus de mots-clés (issue #89) : les pastilles vertes ressortaient sur
 * toutes les offres, et « 3 manquants » disait toujours 3, le prompt en
 * demandant « 2 à 3 ». Ils restent sur la fiche de l'offre.
 *
 * Le point bloquant ne s'affiche que quand `match.result` existe, c'est-à-dire
 * seulement en Radar : `listOffers` ne transporte le résultat complet
 * qu'avec un `profileId` (cf. son commentaire), pour ne pas alourdir la pile
 * Console d'un JSON par consultant × offre sur cent cinquante lignes.
 */
function LigneOffre({
  offre,
  choisie,
  onBasculer,
  onTrancher,
  onDone,
}: {
  offre: OfferRow
  /** `undefined` : pas de case à cocher (Radar, un seul profil, rien à mettre en lot). */
  choisie?: boolean
  onBasculer?: () => void
  onTrancher: (o: OfferRow, s: 'later' | 'skip') => Promise<void>
  onDone: () => void
}) {
  const profilUnique = useProfilUnique()
  const t = useT()
  const profils = [...offre.matches].sort((a, b) => b.score - a.score)
  const meilleur = profils[0]
  const ton = scoreTone(meilleur?.score ?? -1)
  const bloquant = (meilleur?.result?.dealBreakers ?? [])[0]
  const lieu = offre.location?.trim()

  return (
    <div className={`of-row${choisie ? ' picked' : ''}`}>
      {onBasculer && (
        <span className="of-pick">
          <Checkbox label={t('decisions.selectOffer', { title: offre.title })} checked={!!choisie} onChange={onBasculer} />
        </span>
      )}

      <ScoreRing score={meilleur?.score ?? 0} tone={ton} size={34} />
      <LogoEntreprise nom={offre.company} logo={offre.logo} className="pc-logo of-logo" />

      <div className="of-body">
        <div className="of-top">
          {/* Un VRAI lien, et toute la ligne avec lui (son ::after la couvre) :
              clic molette et « ouvrir dans un nouvel onglet » marchent. Les
              actions passent au-dessus. */}
          <a
            className={`of-title${offre.read ? '' : ' unread'}`}
            href={`#/offre/${offre.id}`}
          >
            {offre.title}
          </a>
          <span className="of-meta">
            <Employeur nom={offre.company} source={offre.source_name} />
            {lieu && (
              <>
                <span className="of-sep">·</span>
                <span className="of-lieu"><MapPin size={11} />{lieu}</span>
              </>
            )}
            <span className="of-sep">·</span>
            {relTime(offre.first_seen_at)}
            {/* Lue quand personne ne suivait son site (issue #131) : pas fraîche. */}
            {offre.sourceARelire && (
              <>
                <span className="of-sep">·</span>
                <span className="of-vieux" title={t('offre.staleSource', { date: jourDeLecture(offre.sourceARelire) })}>
                  {t('offre.staleShort', { date: jourDeLecture(offre.sourceARelire) })}
                </span>
              </>
            )}
            {mentionSignaux(offre.signaux, t) && (
              <>
                <span className="of-sep">·</span>
                <span className="of-vieux">{mentionSignaux(offre.signaux, t)}</span>
              </>
            )}
          </span>
          {!profilUnique && meilleur && (
            <span className="of-who" title={profils.map((p) => `${p.name} : ${p.score}`).join('\n')}>
              <span className="miniav" style={teinte(meilleur.name)}>{initials(meilleur.name)}</span>
              {meilleur.name.split(' ')[0]}
              {profils.length > 1 && <span className="dl-more">+{profils.length - 1}</span>}
            </span>
          )}
        </div>

        {bloquant && (
          <div className="of-bloquant">
            <EyeOff size={12} />{bloquant}
          </div>
        )}

      </div>

      {/* Toujours visibles, en toutes lettres. C'était le reproche précis
          fait à l'ancienne ligne dense (« pas assez d'actions »). */}
      <div className="of-acts">
        <ProposerProfil offerId={offre.id} compact profils={profils} onDone={onDone} />
        <ActionButton size="sm" title={t('decisions.laterTitle')} onClick={() => onTrancher(offre, 'later')}>
          <Clock3 size={13} />{t('decisions.later')}
        </ActionButton>
        <ActionButton size="sm" onClick={() => onTrancher(offre, 'skip')}>
          <EyeOff size={13} />{t('decisions.skip')}
        </ActionButton>
        {offre.url && (
          <a className="of-ico" href={offre.url} target="_blank" rel="noopener" title={t('veille.viewOriginal')}>
            <ExternalLink size={13} />
          </a>
        )}
      </div>
    </div>
  )
}

/**
 * Les offres écartées, repliées, avec de quoi revenir sur la décision.
 *
 * Chargée à part (`scope=skip`) plutôt que filtrée depuis `toutes` : la pile
 * ne transporte que les offres actives (cf. son commentaire d'ouverture), les
 * écartées n'y sont donc simplement pas. Le pendant exact de la réserve
 * « à revoir » de `Suivi.tsx`, même mécanique de reprise (`offer.status`
 * remis à `todo`), parce que c'est la même décision qui se corrige.
 */
function Ecartees({ onDone }: { onDone: () => void }) {
  const data = useData(() => get<OfferRow[]>('/api/offers?scope=skip'), [])
  const [ouvert, setOuvert] = useState(false)
  const toast = useToast()
  const t = useT()
  const mots = useMots()
  const profilUnique = useProfilUnique()
  const rows = data.data ?? []

  if (data.loading || rows.length === 0) return null

  async function reprendre(o: OfferRow) {
    await post(`/api/offers/${o.id}/status`, { status: 'todo' })
    toast(t(profilUnique ? 'decisions.reopenedToastRadar' : 'decisions.reopenedToast', { title: o.title }))
    data.reload()
    onDone()
  }

  return (
    <section className="ksec closed" style={{ marginTop: 'var(--s5)' }}>
      <button className="ksec-h as-btn" aria-expanded={ouvert} onClick={() => setOuvert((v) => !v)}>
        {ouvert ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <b>{mots('pile.ecartees')}</b>
        <span className="n">{rows.length}</span>
        <span className="ksec-a">{t('decisions.skippedHint')}</span>
      </button>
      {ouvert && rows.map((o) => {
        const meilleur = o.matches[0]
        return (
          <div className="krow" key={o.id}>
            <button className="krow-main" onClick={() => navigate(`#/offre/${o.id}`)}>
              <LogoEntreprise nom={o.company} logo={o.logo} />
              <span className="pc-id">
                <span className="pc-t">{o.title}</span>
                <span className="pc-c"><Employeur nom={o.company} source={o.source_name} /> · {profilUnique ? relTime(o.first_seen_at) : t('decisions.skippedAt', { when: relTime(o.first_seen_at) })}</span>
              </span>
            </button>
            {meilleur && <Score value={meilleur.score} />}
            <ActionButton size="sm" title={profilUnique ? mots('suivi.remettre') : t('decisions.reopenTitle')} onClick={() => reprendre(o)}>
              <Undo2 size={13} />{t('decisions.reopen')}
            </ActionButton>
          </div>
        )
      })}
    </section>
  )
}

function Select({
  value,
  onChange,
  all,
  options,
}: {
  value: string
  onChange: (v: string) => void
  all: string
  options: string[]
}) {
  if (options.length === 0) return null
  return (
    <select
      className={`chip${value !== 'all' ? ' on' : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={all}
    >
      <option value="all">{all}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  )
}
